import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ContactlessIcon from '@mui/icons-material/Contactless';
import { scanNfcLoginCard } from './nfcLogin';

interface NfcScanDialogProps {
    open: boolean;
    onClose: () => void;
    onToken: (token: string) => void;
}

/**
 * Reads an NFC login card and reports the token via `onToken`. Web NFC is
 * Chromium-on-Android only, so callers must gate this behind `isNfcSupported()`
 * — on every other platform the card's URL is the way in. Scanning stops when
 * the dialog closes or unmounts.
 */
export default function NfcScanDialog({
    open,
    onClose,
    onToken
}: NfcScanDialogProps) {
    const intl = useIntl();
    const readRef = useRef(false);
    // Keep the latest callback without restarting the scan each render.
    const onTokenRef = useRef(onToken);
    const [error, setError] = useState<string | null>(null);
    // A card that holds no login — recoverable, so it does not stop the scan.
    const [wrongCard, setWrongCard] = useState(false);
    // Bumping this re-runs the scan effect — used to retry after a denial.
    const [retryNonce, setRetryNonce] = useState(0);

    useEffect(() => {
        onTokenRef.current = onToken;
    }, [onToken]);

    function handleRetry() {
        setError(null);
        setWrongCard(false);
        setRetryNonce((nonce) => nonce + 1);
    }

    useEffect(() => {
        if (!open) {
            return;
        }
        // No state reset here: the dialog is mounted fresh each time it opens,
        // and `handleRetry` clears the previous attempt's messages itself.
        readRef.current = false;
        const controller = new AbortController();

        scanNfcLoginCard((token) => {
            if (readRef.current) {
                return;
            }
            if (!token) {
                setWrongCard(true);
                return;
            }
            readRef.current = true;
            onTokenRef.current(token);
        }, controller.signal).catch(() => {
            if (!controller.signal.aborted) {
                setError(intl.formatMessage({ id: 'auth.nfc.error' }));
            }
        });

        return () => controller.abort();
    }, [open, retryNonce, intl]);

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                <FormattedMessage id="auth.nfc.scan.title" />
            </DialogTitle>
            <DialogContent
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                {error ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2
                        }}
                    >
                        <Alert
                            severity="error"
                            sx={{ width: '100%', maxWidth: 480 }}
                        >
                            {error}
                        </Alert>
                        <Button variant="contained" onClick={handleRetry}>
                            <FormattedMessage id="auth.nfc.requestAccess" />
                        </Button>
                    </Box>
                ) : (
                    <>
                        {wrongCard && (
                            <Alert severity="warning">
                                <FormattedMessage id="auth.nfc.wrongCard" />
                            </Alert>
                        )}
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 3
                            }}
                        >
                            <ContactlessIcon
                                sx={{
                                    fontSize: 96,
                                    color: 'primary.main',
                                    animation:
                                        'nfcPulse 1.6s ease-in-out infinite',
                                    '@keyframes nfcPulse': {
                                        '0%, 100%': { opacity: 0.35 },
                                        '50%': { opacity: 1 }
                                    }
                                }}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    maxWidth: 360
                                }}
                            >
                                <FormattedMessage id="auth.nfc.scan.hint" />
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.cancel" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
